/* Game shell: hint + board + keyboard + end screen, plus the global keyboard
   handler. Mounted as the only child of <main>. */

import { useEffect } from "preact/hooks";
import {
  backspace,
  buyHint,
  moveCursor,
  newGame,
  status,
  submit,
  typeLetter,
} from "../game/state";
import { BoardEffects } from "../effects/BoardEffects";
import { Board } from "./Board";
import { Hint } from "./Hint";
import { Keyboard } from "./Keyboard";
import { PlayAgain } from "./PlayAgain";
import { ShareGrid } from "./ShareGrid";

export function Game() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore typing into actual inputs (none today; future-proof).
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "Enter") {
        // Let focused buttons handle Enter natively (theme toggle,
        // Play Again, on-screen keyboard). Otherwise apply the global
        // submit / newGame shortcut.
        if (target && target.tagName === "BUTTON") return;
        e.preventDefault();
        if (status.value === "playing") submit();
        else newGame();
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        backspace();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveCursor(-1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        moveCursor(1);
        return;
      }
      if (e.key === "?" && status.value === "playing") {
        e.preventDefault();
        buyHint();
        return;
      }
      if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
        e.preventDefault();
        typeLetter(e.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div class="game">
      <Hint />
      <Board />
      <Keyboard />
      <PlayAgain />
      <ShareGrid />
      <BoardEffects />
    </div>
  );
}
